import { useCallback, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from 'core/i18n/I18nProvider';
import { IconClose } from 'shared/assets/icons';
import { useFocusTrap } from 'shared/hooks/useFocusTrap';
import { useScrollLock } from 'shared/hooks/useScrollLock';

export function Modal({
  open,
  title,
  children,
  onClose,
  size = 'default',
  preventClose = false,
  labelledBy,
  describedBy,
}) {
  const { t } = useI18n();
  const panelRef = useRef(null);
  const titleId = useId();
  const handleClose = useCallback(() => {
    if (preventClose) {
      return;
    }
    onClose?.();
  }, [onClose, preventClose]);

  useFocusTrap(open, panelRef, handleClose);
  useScrollLock(open);

  if (!open) {
    return null;
  }

  const node = (
    <div className="modal-backdrop" data-testid="modal-backdrop" role="presentation">
      <div
        ref={panelRef}
        className={`modal modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
      >
        <div className="modal-head">
          {title ? <h2 id={labelledBy ? undefined : titleId}>{title}</h2> : <span id={titleId} className="sr-only">{t('confirm.title')}</span>}
          {preventClose ? null : (
            <button type="button" className="btn btn-icon" onClick={handleClose} aria-label={t('actions.close')}>
              <IconClose />
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
